import React, { useEffect, useState, createContext, useContext } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
// The chat platforms Giga lives inside. These cycle in the home hero headline
// ("...your team in ___") to show Giga isn't locked to a single workspace.
// Order matters — Slack leads since it's the primary positioning. Each carries
// its real brand mark + chip color so the platform reads instantly as a logo,
// not just a word.
type Platform = {
  name: string;
  // Inline brand glyph so it always renders in full color (no CDN fallback).
  logo: React.ReactNode;
  // Brand-tinted "liquid glass" pill: a translucent base tint, a soft border,
  // and a colored glow so each platform reads as its own frosted chip.
  bg: string;
  border: string;
  glow: string;
  text: string;
  // How the matching composer SEND BUTTON is styled, so other UI (e.g. the
  // typewriter prompt card's send button) can mirror the real product:
  //   Slack → solid green, square-ish (rounded-md)
  //   Teams → solid purple, circular (rounded-full)
  send: {
    color: string;
    shape: 'square' | 'round';
  };
};
const SlackGlyph = () => <svg viewBox="0 0 24 24" className="h-[0.7em] w-[0.7em] shrink-0" aria-hidden="true" data-id="element-1853" data-mp="32c8r5gqy8qcs">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-1854" data-mp="2xfiaosb8fsqu" />
    <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" fill="#36C5F0" data-id="element-1855" data-mp="3l64hyhdex8zs" />
    <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-1856" data-mp="24qthrgkbytq6" />
    <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-1857" data-mp="275ro62udn71j" />
  </svg>;
const TeamsGlyph = () => <svg viewBox="0 0 2228.833 2073.333" className="h-[0.7em] w-[0.7em] shrink-0" aria-hidden="true" data-id="element-1858" data-mp="1jfdr1xwxqi03">
    <path fill="#5059C9" d="M1554.637 777.5h575.713c54.391 0 98.483 44.092 98.483 98.483v524.398c0 199.901-162.051 361.952-361.952 361.952h-1.711c-199.901.028-361.975-162-362.004-361.901V828.971c.001-28.427 23.045-51.471 51.471-51.471z" data-id="element-1859" data-mp="1o6m6baky4xl6" />
    <circle fill="#5059C9" cx="1943.75" cy="440.583" r="233.25" data-id="element-1860" data-mp="1egu908lgpqd0" />
    <circle fill="#7B83EB" cx="1218.083" cy="336.917" r="336.917" data-id="element-1861" data-mp="10huj6xm7r5f9" />
    <path fill="#7B83EB" d="M1667.323 777.5H717.01c-53.743 1.33-96.257 45.931-95.01 99.676v598.105c-7.505 322.519 247.657 590.16 570.167 598.053 322.51-7.893 577.671-275.534 570.167-598.053V877.176c1.245-53.745-41.268-98.346-95.011-99.676z" data-id="element-1862" data-mp="14i4wywg8z04o" />
    <path opacity=".1" d="M1244 777.5v838.145c-.258 38.435-23.549 72.964-59.09 87.598a91.856 91.856 0 0 1-35.967 7.257H667.613c-6.738-17.105-12.958-34.21-18.142-51.833a631.287 631.287 0 0 1-27.472-183.49V877.02c-1.246-53.659 41.198-98.19 94.855-99.52H1244z" data-id="element-1863" data-mp="gwb03rm66znp" />
    <path opacity=".2" d="M1192.167 777.5v889.978a91.802 91.802 0 0 1-7.257 35.967c-14.634 35.541-49.163 58.833-87.598 59.09H691.975c-8.812-17.105-17.105-34.21-24.362-51.833-7.257-17.623-12.958-34.21-18.142-51.833a631.287 631.287 0 0 1-27.472-183.49V877.02c-1.246-53.659 41.198-98.19 94.855-99.52h475.313z" data-id="element-1864" data-mp="14sot40ic0cvt" />
    <path opacity=".2" d="M1192.167 777.5v786.312c-.395 52.223-42.632 94.46-94.855 94.855H649.472a631.287 631.287 0 0 1-27.472-183.49V877.02c-1.246-53.659 41.198-98.19 94.855-99.52h475.312z" data-id="element-1865" data-mp="19j5ce18u2sq3" />
    <path opacity=".2" d="M1140.333 777.5v786.312c-.395 52.223-42.632 94.46-94.855 94.855H649.472a631.287 631.287 0 0 1-27.472-183.49V877.02c-1.246-53.659 41.198-98.19 94.855-99.52h423.478z" data-id="element-1866" data-mp="1ikvblt8yivyf" />
    <path opacity=".1" d="M1244 509.522v163.275c-8.812.518-17.105 1.037-25.917 1.037-8.812 0-17.105-.518-25.917-1.037a286.747 286.747 0 0 1-51.833-10.371c-104.226-24.674-190.105-97.794-231.265-196.973a288.02 288.02 0 0 1-16.587-51.833h261.265c52.305.198 94.657 42.549 94.854 94.855z" data-id="element-1867" data-mp="26ntba99815ev" />
    <path opacity=".2" d="M1192.167 561.355v111.442a286.747 286.747 0 0 1-51.833-10.371c-104.226-24.674-190.105-97.794-231.265-196.973h188.244c52.305.198 94.656 42.55 94.854 94.902z" data-id="element-1868" data-mp="1jnlp4lr0ntpo" />
    <path opacity=".2" d="M1192.167 561.355v111.442a286.747 286.747 0 0 1-51.833-10.371c-104.226-24.674-190.105-97.794-231.265-196.973h188.244c52.305.198 94.656 42.55 94.854 94.902z" data-id="element-1869" data-mp="1nr0gu9obhyiy" />
    <path opacity=".2" d="M1140.333 561.355v103.073a286.747 286.747 0 0 1-51.833-10.371c-104.226-24.674-190.105-97.794-231.265-196.973h136.41c52.306.198 94.658 42.549 94.855 94.902z" data-id="element-1870" data-mp="1xood541kne38" />
    <linearGradient id="teamsg" gradientUnits="userSpaceOnUse" x1="198.099" y1="1683.0726" x2="942.2344" y2="394.2607" gradientTransform="matrix(1 0 0 -1 0 2075.3333)" data-id="element-1871" data-mp="390yvegiovrtf">
      <stop offset="0" stopColor="#5a62c3" data-id="element-1872" data-mp="sftu2kwpqzqo" />
      <stop offset=".5" stopColor="#4d55bd" data-id="element-1873" data-mp="sgvg1kf9p6n6" />
      <stop offset="1" stopColor="#3940ab" data-id="element-1874" data-mp="novancgfhlj7" />
    </linearGradient>
    <path fill="url(#teamsg)" d="M95.01 561.355h950.312c52.473 0 95.01 42.538 95.01 95.01v950.312c0 52.473-42.538 95.01-95.01 95.01H95.01c-52.473 0-95.01-42.538-95.01-95.01V656.365c0-52.472 42.538-95.01 95.01-95.01z" data-id="element-1875" data-mp="nn0ppy55ptf4" />
    <path fill="#fff" d="M820.211 922.038H630.241v517.297H509.211V922.038H320.123V821.456h500.088z" data-id="element-1876" data-mp="9pvp4do7pmr9" />
  </svg>;
export const PLATFORMS: Platform[] = [{
  name: 'Slack',
  logo: <SlackGlyph data-id="element-1877" data-mp="4slsoe2eoatx" />,
  // Opaque, light aubergine-tinted base — reads as a frosted glass pane but
  // is fully solid so the dotted background never shows through.
  bg: '#F0EAF2',
  border: 'rgba(74,21,75,0.22)',
  glow: 'rgba(74,21,75,0.18)',
  text: '#4A154B',
  // Slack's send button: green, square-ish.
  send: {
    color: '#007a5a',
    shape: 'square'
  }
}];
// ── Shared hero cadence ──────────────────────────────────────────────────
// The hero headline has TWO animated lockups — the tool-icon stack
// (RotatingStack) on the first line and the platform pill (RotatingPlatform)
// on the second. Cycling them on independent timers made them flicker against
// each other. Instead they share ONE clock and TAKE TURNS: a single beat
// counter ticks on a calm interval, the tools swap on EVEN beats and the
// platform swaps on ODD beats — a call-and-response rhythm where only one
// thing moves at a time. The pill's index is derived from the beat so the
// headline pill and the prompt card's send button still flip together.
//
// HERO_BEAT_MS is the spacing between turns. Each lockup therefore changes
// every 2 beats (≈ one full Slack↔Teams or tool-set swap per 2× this value).
const HERO_BEAT_MS = 1600;
type HeroCadence = {
  // Monotonically increasing beat counter shared by both lockups.
  beat: number;
  // Active platform, derived from the beat (advances on ODD beats).
  index: number;
  platform: Platform;
};
const PlatformCycleContext = createContext<HeroCadence | null>(null);
export function PlatformCycleProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const reduce = useReducedMotion();
  const [beat, setBeat] = useState(0);
  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => {
      setBeat(b => b + 1);
    }, HERO_BEAT_MS);
    return () => clearInterval(id);
  }, [reduce]);
  // The platform advances once every two beats, on the ODD beats, so it
  // alternates with the tool stack (which advances on the EVEN beats).
  const index = Math.floor((beat + 1) / 2) % PLATFORMS.length;
  return <PlatformCycleContext.Provider value={{
    beat,
    index,
    platform: PLATFORMS[index]
  }} data-id="element-1878" data-mp="25m7wzub3k1hr">
      {children}
    </PlatformCycleContext.Provider>;
}
// Returns the shared hero cadence (beat + active platform), or null if there
// is no provider above (callers then fall back to their own internal timer).
export function usePlatformCycle(): HeroCadence | null {
  return useContext(PlatformCycleContext);
}
export function RotatingPlatform() {
  const reduce = useReducedMotion();
  // Prefer the shared cycle (so the headline pill and the prompt card's send
  // button flip together); fall back to a private cycle when used standalone.
  const shared = usePlatformCycle();
  const [localIndex, setLocalIndex] = useState(0);
  useEffect(() => {
    if (reduce || shared) return;
    const id = setInterval(() => {
      setLocalIndex(i => (i + 1) % PLATFORMS.length);
    }, 2200);
    return () => clearInterval(id);
  }, [reduce, shared]);
  const index = shared ? shared.index : localIndex;
  return (
    // Reserve the width of the WIDEST platform chip by stacking every option
    // invisibly in the same grid cell. This keeps the total sentence width
    // constant so the preceding words ("...your team in") never reflow or
    // re-center — only the platform chip swaps. The chip is left-anchored so
    // it always starts at the same x and any extra room sits to its right.
    <span className="relative inline-grid place-items-start align-baseline not-italic" data-id="element-1879" data-mp="3dlm4s8yi3hni">
      {/* Width/height sizer: every chip is stacked in the SAME grid cell (each
                         col-start-1/row-start-1), so the reserved box is exactly ONE chip tall
                         and as wide as the WIDEST chip — never two chips tall. This keeps the
                         visible layer's box a single, stable line box. */}
      <span className="invisible col-start-1 row-start-1 grid" aria-hidden="true" data-id="element-1880" data-mp="17csp1u0vphf">
        {PLATFORMS.map(p => <span key={p.name} className="col-start-1 row-start-1 block" data-id="element-1881" data-mp="3shbk5o96kwj9">
            <PlatformChip platform={p} data-id="element-1882" data-mp="3nk1ty6dywgud" />
          </span>)}
      </span>
      {/* The visible chip layer. It is ABSOLUTELY laid over the invisible
                             width-reserving sizer above (which holds the WIDEST chip's width),
                             so this layer's box never changes size as chips mount/unmount.
                             Each chip is itself absolutely pinned to the left edge of that box,
                             meaning every chip's x-origin is identical and fixed — a narrower
                             chip (Slack) can no longer shift horizontally when the wider one
                             (Teams) exits. We OVERLAP the outgoing and incoming chips (no
                             `mode="wait"`) so the pill is never empty for a frame. */}
      <span className="col-start-1 row-start-1 block relative" data-id="element-1883" data-mp="gj0jdg3tgzy3">
        <AnimatePresence initial={false} data-id="element-1884" data-mp="v8pofmf15o3n">
          <motion.span key={PLATFORMS[index].name} initial={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: '0.4em',
            filter: 'blur(4px)'
          }} animate={{
            opacity: 1,
            y: 0,
            filter: 'blur(0px)'
          }} exit={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: '-0.4em',
            filter: 'blur(4px)'
          }} transition={{
            duration: 0.45,
            ease: [0.16, 1, 0.3, 1]
          }} className="absolute left-0 top-0 inline-block whitespace-nowrap" data-id="element-1885" data-mp="v7mx5ddspjn7">
            <PlatformChip platform={PLATFORMS[index]} data-id="element-1886" data-mp="19x7ul3jwali1" />
          </motion.span>
        </AnimatePresence>
      </span>
    </span>
  );
}
// A "liquid glass" pill that pairs the brand logo with the platform name, so
// the headline makes it instantly obvious Giga lives in Slack or Teams. The
// chip is a frosted, brand-tinted pane: a translucent base, a backdrop blur, a
// glossy specular sheen along the top, and a soft brand-colored glow.
function PlatformChip({
  platform
}: {
  platform: Platform;
}) {
  return <span className="relative inline-flex items-center gap-[0.3em] overflow-hidden rounded-[0.55em] px-[0.45em] py-[0.08em] align-baseline not-italic font-semibold leading-none border [backdrop-filter:blur(8px)_saturate(1.4)] [-webkit-backdrop-filter:blur(8px)_saturate(1.4)] text-[0.74em]" style={{
    backgroundColor: platform.bg,
    borderColor: platform.border,
    color: platform.text,
    // Render the pill notably smaller than the surrounding headline
    // (text-[0.74em] above) — since the whole chip is em-relative, this
    // shrinks its padding, logo, and radius together, lowering its overall
    // height. Paired with a slightly larger downward nudge so the top of
    // the pill clears the row of tool icons sitting on the line above and
    // the two never touch.
    transform: 'translateY(0.05em)',
    boxShadow: `0 1px 1px rgba(255,255,255,0.5) inset, 0 -1px 1px rgba(0,0,0,0.04) inset, 0 4px 14px -4px ${platform.glow}, 0 1px 3px rgba(0,0,0,0.06)`
  }} data-id="element-1887" data-mp="v3wds733t0yl">
      {/* Glossy specular sheen across the top half — the "liquid glass" gleam */}
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-[0.55em]" style={{
      background: 'linear-gradient(to bottom, rgba(255,255,255,0.55), rgba(255,255,255,0.08) 45%, rgba(255,255,255,0) 60%)'
    }} data-id="element-1888" data-mp="1mfbukkwm1d1r" />
      {/* Soft highlight blob in the top-left for extra liquid depth */}
      <span aria-hidden="true" className="pointer-events-none absolute -left-[10%] -top-[40%] h-[120%] w-[55%] rounded-full" style={{
      background: 'radial-gradient(closest-side, rgba(255,255,255,0.5), rgba(255,255,255,0) 70%)'
    }} data-id="element-1889" data-mp="1tlxhst6gk08" />
      <span className="relative flex items-center" data-id="element-1890" data-mp="fnmnny62cyoz">{platform.logo}</span>
      <span className="relative" data-id="element-1891" data-mp="fph7ta9igms0">{platform.name}</span>
    </span>;
}